import { createApiRef } from "@backstage/core";
import { DiscoveryApi, IdentityApi } from "@backstage/core-plugin-api";
import axios from "axios";
import { OpsgenieIncident, OpsgeniePostMortem, OpsgenieUser } from "../types";

export const opsgenieApiRef = createApiRef<OpsgenieApi>({
  id: "plugin.opsgenie-api.service",
  description: "Used by plugins to make requests to Opsgenie backend",
});

type Options = {
  discoveryApi: DiscoveryApi;
  identityApi: IdentityApi;
};

export class OpsgenieApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;

  constructor(options: Options) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
  }

  /** Retrieve the primary URL  */
  private async getUrl(): Promise<string> {
    return await this.discoveryApi.getBaseUrl("opsgenie-api");
  }

  private filterFiredrills(data: OpsgenieIncident[]): OpsgenieIncident[] {
    return data.filter(
      (incident: OpsgenieIncident) => !incident.tags.includes("firedrill")
    );
  }

  public async getTeamNames(): Promise<{ status: string; data: string[] }> {
    const { token } = await this.identityApi.getCredentials();
    const response = await axios.get(`${await this.getUrl()}/getTeamNames`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data;
  }

  public async getUser(identifier: string): Promise<OpsgenieUser> {
    const { token } = await this.identityApi.getCredentials();
    const response = await axios.get(
      `${await this.getUrl()}/getUser?identifier=${identifier}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    return response.data.data;
  }

  public async getUsers(): Promise<OpsgenieUser[]> {
    const { token } = await this.identityApi.getCredentials();
    const response = await axios.get(`${await this.getUrl()}/getUsers`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    return response.data.data;
  }

  public async getTeamNameById(identifier: string): Promise<string> {
    const { token } = await this.identityApi.getCredentials();
    const response = await axios.get(
      `${await this.getUrl()}/getTeamNameById?id=${identifier}`,
      {
        headers: { Authorization: `Bearer ${token}` },
      }
    );
    return response.data.data;
  }

  async getIncidents({
    filterFiredrills = true,
    resolved = false,
    fromDaysInPast = 7,
  }: {
    filterFiredrills?: boolean;
    resolved?: boolean;
    fromDaysInPast?: number;
  }): Promise<OpsgenieIncident[]> {
    let query = `fromDaysInPast=${fromDaysInPast}`;

    if (resolved) {
      query += "&resolved=true";
    }

    const { token } = await this.identityApi.getCredentials();

    const response = await axios.get(
      `${await this.getUrl()}/getIncidents${query ? `?${query}` : ""}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    let data = response.data.data;
    if (filterFiredrills) {
      data = this.filterFiredrills(data);
    }
    return data;
  }

  async createPostMortem({
    incidentId,
    userId,
  }: {
    incidentId: string;
    userId: string;
  }): Promise<{ status: string; error?: string; data: string }> {
    const { token } = await this.identityApi.getCredentials();

    const request = await axios.post(
      `${await this.getUrl()}/createPostMortem`,
      {
        incidentId,
        userId,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const data = request.data;
    return data;
  }

  async registerPostMortem({
    incidentId,
    postMortemId,
  }: {
    incidentId: string;
    postMortemId: string;
  }): Promise<{
    status: string;
    error?: string;
    data: {
      message: string;
      incidentId: string;
      postMortemId: string;
    };
  }> {
    const { token } = await this.identityApi.getCredentials();

    const request = await axios.post(
      `${await this.getUrl()}/registerPostMortem`,
      {
        incidentId,
        postMortemId,
      },
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const data = request.data;
    return data;
  }

  async getPostMortemIdByIncidentId({
    incidentId,
  }: {
    incidentId: string;
  }): Promise<{ status: string; error?: string; data: { data: string } }> {
    const { token } = await this.identityApi.getCredentials();
    const request = await axios.get(
      `${await this.getUrl()}/getPostMortemIdByIncidentId?incidentId=${incidentId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const data = request.data;
    return data;
  }

  async getPostMortem({ postMortemId }: { postMortemId: string }): Promise<{
    status: string;
    error?: string;
    data: { data: OpsgeniePostMortem };
  }> {
    const { token } = await this.identityApi.getCredentials();
    const request = await axios.get(
      `${await this.getUrl()}/getPostMortem?postMortemId=${postMortemId}`,
      { headers: { Authorization: `Bearer ${token}` } }
    );
    const data = request.data;
    return data;
  }
}
